import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { IconDatabaseExport, IconFileTypePdf, IconFileSpreadsheet } from '@tabler/icons-react';
import { ChevronDown, Download, Database, Bot } from 'lucide-react';
import { toast } from 'sonner';

export interface BulkExportData {
  keywordResearch?: {
    related?: any[];
    ideas?: any[];
    suggestions?: any[];
    subtopics?: any[];
    paa?: any[];
    trends?: any[];
    serp?: any[];
  };
  aiAnalysis?: {
    categories?: any[];
    clusters?: any[];
    contentIdeas?: any[];
  };
  metadata: {
    workspaceName: string;
    exportDate: string;
    totalRecords: number;
  };
}

interface BulkExportButtonProps {
  data: BulkExportData;
  workspaceName: string;
  className?: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'default' | 'lg';
  disabled?: boolean;
}

const BulkExportButton: React.FC<BulkExportButtonProps> = ({
  data,
  workspaceName,
  className = '',
  variant = 'outline',
  size = 'default',
  disabled = false,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  // Calculate total records
  const getTotalRecords = (scope: 'keyword_research' | 'ai_analysis' | 'complete') => {
    let total = 0;
    
    if (scope === 'keyword_research' || scope === 'complete') {
      if (data.keywordResearch) {
        Object.values(data.keywordResearch).forEach(items => {
          total += items?.length || 0;
        });
      }
    }
    
    if (scope === 'ai_analysis' || scope === 'complete') {
      if (data.aiAnalysis) {
        Object.values(data.aiAnalysis).forEach(items => {
          total += items?.length || 0;
        });
      }
    }
    
    return total;
  };

  const handleBulkExport = async (
    format: 'excel' | 'pdf',
    scope: 'keyword_research' | 'ai_analysis' | 'complete'
  ) => {
    setIsExporting(true);
    
    try {
      const totalRecords = getTotalRecords(scope);
      
      if (totalRecords === 0) {
        toast.error('No data available for export');
        return;
      }

      // Import the bulk export functions dynamically
      const { exportBulkToExcel, exportBulkToPDF } = await import('@/lib/bulkExportUtils');
      
      const filename = `${workspaceName.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${scope}_export`;
      
      if (format === 'excel') {
        await exportBulkToExcel(data, scope, { filename, workspaceName });
        toast.success(`Excel file exported successfully (${totalRecords} records)`);
      } else {
        await exportBulkToPDF(data, scope, { filename, workspaceName });
        toast.success(`PDF exported successfully (${totalRecords} records)`);
      }
    } catch (error: unknown) {
      console.error('Bulk export failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(`Export failed: ${errorMessage}`);
    } finally {
      setIsExporting(false);
    }
  };

  const keywordResearchCount = getTotalRecords('keyword_research');
  const aiAnalysisCount = getTotalRecords('ai_analysis');
  const totalCount = getTotalRecords('complete');
  const hasData = totalCount > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`flex items-center gap-2 ${className}`}
          disabled={disabled || !hasData || isExporting}
        >
          {isExporting ? (
            <Download className="h-4 w-4 animate-pulse" />
          ) : (
            <IconDatabaseExport className="h-4 w-4" />
          )}
          <span>
            {isExporting ? 'Exporting...' : 'Bulk Export'}
          </span>
          {hasData && (
            <span className="hidden md:inline text-muted-foreground">
              ({totalCount})
            </span>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>Workspace Export Options</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Complete Workspace Export */}
        {totalCount > 0 && (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <span>Complete Workspace</span>
                <span className="text-muted-foreground text-xs">({totalCount})</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => handleBulkExport('excel', 'complete')}
                  disabled={!hasData || isExporting}
                  className="flex items-center gap-2"
                >
                  <IconFileSpreadsheet className="h-4 w-4" />
                  <span>Excel (Multi-Sheet)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkExport('pdf', 'complete')}
                  disabled={!hasData || isExporting}
                  className="flex items-center gap-2"
                >
                  <IconFileTypePdf className="h-4 w-4" />
                  <span>PDF (Multi-Page)</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
          </>
        )}

        {/* Keyword Research Export */}
        {keywordResearchCount > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              <span>Keyword Research</span>
              <span className="text-muted-foreground text-xs">({keywordResearchCount})</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                onClick={() => handleBulkExport('excel', 'keyword_research')}
                disabled={!hasData || isExporting}
                className="flex items-center gap-2"
              >
                <IconFileSpreadsheet className="h-4 w-4" />
                <span>Excel (All Tabs)</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleBulkExport('pdf', 'keyword_research')}
                disabled={!hasData || isExporting}
                className="flex items-center gap-2"
              >
                <IconFileTypePdf className="h-4 w-4" />
                <span>PDF (All Tabs)</span>
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {/* AI Analysis Export */}
        {aiAnalysisCount > 0 && (
          <>
            {keywordResearchCount > 0 && <DropdownMenuSeparator />}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex items-center gap-2">
                <Bot className="h-4 w-4" />
                <span>AI Analysis</span>
                <span className="text-muted-foreground text-xs">({aiAnalysisCount})</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => handleBulkExport('excel', 'ai_analysis')}
                  disabled={!hasData || isExporting}
                  className="flex items-center gap-2"
                >
                  <IconFileSpreadsheet className="h-4 w-4" />
                  <span>Excel (All Tabs)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleBulkExport('pdf', 'ai_analysis')}
                  disabled={!hasData || isExporting}
                  className="flex items-center gap-2"
                >
                  <IconFileTypePdf className="h-4 w-4" />
                  <span>PDF (All Tabs)</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        {!hasData && (
          <DropdownMenuItem disabled className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span>No data available</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BulkExportButton;